import {
  AnimatedContainer,
  AnimatedPage,
  AnimatedPageHeader,
  AnimatedSection
} from '@components/animated'
import { Section } from '@components/index'
import { AccessControl } from '@components/layout'
import { SectionTitle } from '@components/section/SectionTitle'
import {
  PasswordForm,
  PasswordFormValues
} from '@features/auth/components/PasswordForm'
import { Referrals } from '@features/settings/components/Referrals'
import { useChangePassword } from '@services/settings/mutations/useChangePassword'
import { useDeleteAccount } from '@services/settings/mutations/useDeleteAccount'
import { useProfile } from '@services/users/queries/useProfile'
import { Cookie, Lock, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as CookieConsent from 'vanilla-cookieconsent'

import { Button as UIButton } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'

export function Settings() {
  const { t } = useTranslation('settings')
  const { data } = useProfile(true)
  const { mutate: changePassword, isPending: isChangingPassword } =
    useChangePassword()
  // TODO: id shouldn't be required?
  const { mutate: deleteAccount } = useDeleteAccount(data?.id || '')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')

  // Landlords can't delete their account while a flat has an active rental.
  const deletionBlocked = Boolean(
    (data as { has_active_landlord_flat?: boolean } | undefined)
      ?.has_active_landlord_flat
  )

  const handlePasswordChange = (values: PasswordFormValues) => {
    changePassword({
      body: {
        old_password: values.old_password ?? '',
        new_password: values.password,
        password_confirmation: values.password_confirmation
      }
    })
  }

  const showModal = () => {
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setDeletePassword('')
  }

  const handleDelete = (e: React.FormEvent) => {
    e.preventDefault()
    if (!deletePassword) return
    deleteAccount({ password: deletePassword })
    closeModal()
  }

  return (
    <AnimatedPage>
      <div className="containerSmall flex flex-col gap-6">
        <AnimatedPageHeader title={t('title')} />

        <AnimatedContainer className="space-y-5">
          <AnimatedSection>
            <Section
              defaultOpen
              title={
                <SectionTitle
                  icon={<Lock />}
                  title={t('section.changePassword')}
                />
              }
            >
              <PasswordForm
                onSubmit={handlePasswordChange}
                oldPassword
                isPending={isChangingPassword}
              />
            </Section>
          </AnimatedSection>
          <AnimatedSection>
            <Section
              defaultOpen
              title={
                <SectionTitle icon={<Cookie />} title={t('section.cookies')} />
              }
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <p className="m-0 text-sm leading-relaxed text-muted-foreground sm:max-w-xl">
                  {t('cookieSettingsDescription')}
                </p>
                <UIButton
                  variant="outline"
                  onClick={() => CookieConsent.showPreferences()}
                  className="shrink-0"
                >
                  <Cookie className="h-4 w-4" />
                  {t('cookieSettings')}
                </UIButton>
              </div>
            </Section>
          </AnimatedSection>
          {/* Users get a dedicated /user/referrals page; admins keep it here. */}
          <AccessControl role="ADMIN" requirePartner={true}>
            <AnimatedSection>
              <Referrals />
            </AnimatedSection>
          </AccessControl>
          {data?.role !== 'ADMIN' && (
          <AnimatedSection>
            <Section
              defaultOpen
              title={
                <SectionTitle
                  icon={<Trash2 />}
                  title={t('section.deleteAccount')}
                />
              }
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <p
                  className={cn(
                    'm-0 text-sm leading-relaxed sm:max-w-xl',
                    deletionBlocked ? 'text-warning' : 'text-muted-foreground'
                  )}
                >
                  {deletionBlocked
                    ? t('deleteAccountBlocked')
                    : t('deleteAccountWarning')}
                </p>
                <UIButton
                  onClick={showModal}
                  disabled={deletionBlocked}
                  className="shrink-0 bg-destructive text-white hover:bg-destructive-strong"
                >
                  <Trash2 className="h-4 w-4" />
                  {t('deleteAccount')}
                </UIButton>
              </div>

              <Dialog
                open={isModalOpen}
                onOpenChange={(o) => !o && closeModal()}
              >
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t('modal.deleteAccountTitle')}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleDelete} className="space-y-4">
                    <div>
                      <Label className="mb-1.5 block">
                        {t('modal.passwordLabel')}
                      </Label>
                      <Input
                        type="password"
                        autoFocus
                        placeholder={t('modal.passwordPlaceholder')}
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                      />
                    </div>
                    <DialogFooter>
                      <UIButton
                        type="button"
                        variant="ghostDark"
                        onClick={closeModal}
                      >
                        {t('modal.cancel', { defaultValue: 'Zrušiť' })}
                      </UIButton>
                      <UIButton
                        type="submit"
                        disabled={!deletePassword}
                        className="bg-destructive text-white hover:bg-destructive-strong"
                      >
                        <Trash2 className="h-4 w-4" />
                        {t('deleteAccount')}
                      </UIButton>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </Section>
          </AnimatedSection>
          )}
        </AnimatedContainer>
      </div>
    </AnimatedPage>
  )
}
